{
  "title": "How to Manage Platform Users",
  "description": "Search for users, view their roles across competitions, and suspend, reactivate, or force-sign-out their sessions as needed.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, you can look up any user's account status and role relationships, and suspend, reactivate, or force-revoke all of their sessions.",
  "roleNote": "Platform Administrator",
  "prerequisites": ["You hold the platform user-management capability"],
  "pathLabel": "Platform Administration → Users",
  "steps": [
    {
      "title": "1. Search or browse users",
      "description": "Search the user list by name, email, and similar filters; the list is server-side paginated, so it never loads the whole platform's data at once."
    },
    {
      "title": "2. View a user's detail",
      "description": "Open a single user to see their basic profile plus their role relationship in every competition (e.g. a student in Competition A, a judge in Competition B, an organizer admin in Competition C) — each is listed separately, never merged."
    },
    {
      "title": "3a. Suspend an account",
      "description": "Enter a reason (required) and run \"Suspend\"; the account's status changes to suspended and every one of that user's current sessions is revoked at the same time, as a single atomic operation. If that user is the platform's sole active super admin, the suspend is rejected by the system (409 error) to prevent the platform from ending up with zero super admins."
    },
    {
      "title": "3b. Reactivate an account",
      "description": "Run \"Reactivate\" on a suspended account; the reason field is optional. Reactivating doesn't sign the user back in automatically — they need to log in again with their credentials."
    },
    {
      "title": "3c. Force-revoke all sessions",
      "description": "With a reason entered, you can force-revoke a user's current sessions on their own, without changing their account status — commonly used when an account is suspected of being compromised."
    }
  ],
  "afterward": [
    "A suspended user is immediately signed out on every device, and needs an admin to reactivate before they can sign in again",
    "Reactivating restores normal account status, but does not automatically sign the user back in",
    "Suspend/reactivate/force-sign-out are all recorded in the platform audit log; suspend and force-sign-out both require a reason"
  ],
  "nextSteps": "To adjust a user's platform super admin status specifically, see \"How to Manage Platform Super Admins\".",
  "faq": [
    {
      "q": "Does suspending a user delete their past submissions, scores, or audit history?",
      "a": "No. Suspending only changes the account's status and revokes its sessions — it never touches submissions, assignments, scores, results, audit history, or any other existing data."
    },
    {
      "q": "Why can't I suspend a particular user?",
      "a": "The most common reason is the \"last active super admin\" protection: if that user is the platform's sole active super admin, the suspend request is rejected — assign another user super admin status first before suspending this one."
    },
    {
      "q": "Do I need to give a reason to reactivate an account?",
      "a": "No, the reason field for reactivation is optional. Only \"Suspend\" and \"Force-Revoke Sessions\" require a reason, since both restrict a user's access."
    }
  ]
}
